import {
  useGetQuery,
  useLazyGetQuery,
  usePostMutation,
  usePutMutation,
} from "slices/api";
import {
  FIRE_SAFETY_APPLICATION,
  FIRE_SAFETY_BUILDING_OBSERVATION,
  FIRE_SAFETY_DETAIL_OBSERVATION_OPTION,
  REGISTER_BUILDING,
} from "tagTypes";

export const useGetObserverTypeQuery = (query = {}) =>
  useGetQuery({
    endpoint: "/observation-type",
    query,
    tags: [FIRE_SAFETY_BUILDING_OBSERVATION, FIRE_SAFETY_APPLICATION],
  });

export const useGetBuildingSchedulesQuery = (buildingInformationId) =>
  useGetQuery({
    endpoint: `/inspection-observation/building-information/${buildingInformationId}`,
    tags: [FIRE_SAFETY_BUILDING_OBSERVATION, FIRE_SAFETY_APPLICATION],
  });

export const useLazyBuildingScheduleQuery = () => {
  const [getBuildingSchdule, data] = useLazyGetQuery();
  const getBuildingSchduleData = async (buildingInformationId, query = {}) =>
    await getBuildingSchdule(
      {
        endpoint: `/inspection-observation/building-information/${buildingInformationId}`,
        query,
        tags: [FIRE_SAFETY_BUILDING_OBSERVATION, FIRE_SAFETY_APPLICATION],
      },
      true
    ).unwrap();
  return [getBuildingSchduleData, data];
};

export const useLazyFireSafetyBuildingObservationQuery = () => {
  const [getObservation, data] = useLazyGetQuery();
  const getBuildingObservationData = async (scheduleId, query = {}) =>
    await getObservation(
      {
        endpoint: `/inspection-observation/schedule/${scheduleId}`,
        query,
        tags: [FIRE_SAFETY_BUILDING_OBSERVATION, FIRE_SAFETY_APPLICATION],
      },
      true
    ).unwrap();
  return [getBuildingObservationData, data];
};

export const useLazyFireSafetyObservationDataQuery = () => {
  const [getDetailObservation, data] = useLazyGetQuery();
  const getDetailObservationData = async (query = {}) =>
    await getDetailObservation(
      {
        endpoint: "/detail-observation",
        query,
        tags: [FIRE_SAFETY_DETAIL_OBSERVATION_OPTION],
      },
      true
    ).unwrap();
  return [getDetailObservationData, data];
};

export const usePostNoticeGenerateMutation = () => {
  const [genrateNotice, data] = usePostMutation();
  const notice = async (payload, query = {}, options = {}) => {
    const { showToast = true } = options;
    return await genrateNotice({
      endpoint: "/inspection-observation-notice",
      query,
      payload: payload || {},
      toastMessage: showToast ? "Notice generated successfully" : null,
      tags: [REGISTER_BUILDING, FIRE_SAFETY_APPLICATION],
    }).unwrap();
  };
  return [notice, data];
};

export const usePutNoticeDocumentApproveMutation = () => {
  const [updateNoticeStatus, data] = usePutMutation();
  const updateNoticeStatusData = async (payload, query = {}) =>
    await updateNoticeStatus({
      endpoint: "/inspection-observation-notice/approve",
      query,
      payload: payload || {},
      toastMessage: "Notice document status updated successfully",
      tags: [FIRE_SAFETY_APPLICATION],
    }).unwrap();
  return [updateNoticeStatusData, data];
};

export const useLazyGetFireSafetyObservationNotice = () => {
  const [getNoticeObservationData, data] = useLazyGetQuery();
  const getNotice = async (scheduleId, query = {}) =>
    await getNoticeObservationData(
      {
        endpoint: `/inspection-observation-notice/schedule/${scheduleId}`,
        query,
        tags: [FIRE_SAFETY_BUILDING_OBSERVATION, FIRE_SAFETY_APPLICATION],
      },
      true
    ).unwrap();
  return [getNotice, data];
};

export const useLazyGetFireSafetyObservationNoticeQuery = () => {
  const [getNoticeData, data] = useLazyGetQuery();
  const getObservationNotice = async (scheduleId, query = {}) =>
    await getNoticeData(
      {
        endpoint: `/inspection-observation/schedule/${scheduleId}/inspection-observation-notice-document`,
        query,
        tags: [FIRE_SAFETY_BUILDING_OBSERVATION, FIRE_SAFETY_APPLICATION],
      },
      true
    ).unwrap();
  return [getObservationNotice, data];
};

export const usePostFireSafetyBuildingObservationMutation = () => {
  const [buildingObservation, data] = usePostMutation();
  const addBuildingObservation = async (payload, query = {}) =>
    await buildingObservation({
      endpoint: "/inspection-observation",
      query,
      payload: payload || {},
      toastMessage: "Building observation added successfully",
      tags: [FIRE_SAFETY_BUILDING_OBSERVATION, FIRE_SAFETY_APPLICATION],
    }).unwrap();
  return [addBuildingObservation, data];
};

export const usePutSendNoticeMutation = () => {
  const [sendNotice, data] = usePutMutation();
  const sendNoticeData = async (payload) =>
    await sendNotice({
      endpoint: "/inspection-observation-notice/send-notice",
      payload: payload || {},
      toastMessage: "Notice sent successfully",
      tags: [FIRE_SAFETY_BUILDING_OBSERVATION, FIRE_SAFETY_APPLICATION],
    }).unwrap();
  return [sendNoticeData, data];
};

export const usePutSendComplianceReport = () => {
  const [sendReport, data] = usePutMutation();
  const sendComplianceReportData = async (payload) =>
    await sendReport({
      endpoint: "/developer-compliance/send-compliance-report",
      payload: payload || {},
      toastMessage: "Compliance report sent successfully",
      tags: [REGISTER_BUILDING, FIRE_SAFETY_APPLICATION],
    }).unwrap();
  return [sendComplianceReportData, data];
};

export const usePostFireSafetyResolvedObservationPhotographsMutation = () => {
  const [postResolvePhotographs, data] = usePostMutation();
  const addResolvePhotographs = async (payload, query = {}) =>
    await postResolvePhotographs({
      endpoint: "/developer-compliance",
      query,
      payload: payload || {},
      toastMessage: "Resolved details added successfully",
      tags: [FIRE_SAFETY_BUILDING_OBSERVATION, FIRE_SAFETY_APPLICATION],
    }).unwrap();
  return [addResolvePhotographs, data];
};

export const usePutCloseBuildingObservationByIdMutation = () => {
  const [closeInspectionObservation, data] = usePutMutation();
  const updateBuildingObservation = async (observationId, query = {}) =>
    await closeInspectionObservation({
      endpoint: `/inspection-observation/${observationId}/close-inspection-observation`,
      query,
      toastMessage: "Observation closed successfully",
      tags: [FIRE_SAFETY_BUILDING_OBSERVATION, FIRE_SAFETY_APPLICATION],
    }).unwrap();
  return [updateBuildingObservation, data];
};

export const usePutGenerateObservationImagesByScheduleIdMutation = () => {
  const [closeInspectionObservation, data] = usePutMutation();
  const updateBuildingObservation = async (scheduleId, query = {}) =>
    await closeInspectionObservation({
      endpoint: `/inspection-observation/${scheduleId}/generate-comparison-photograph`,
      query,
      toastMessage: "Observation closed successfully",
      tags: [FIRE_SAFETY_BUILDING_OBSERVATION, FIRE_SAFETY_APPLICATION],
    }).unwrap();
  return [updateBuildingObservation, data];
};

export const useLazyGetNoticeUrlByScheduleId = () => {
  const [getNoticeUrl, data] = useLazyGetQuery();
  const getNoticeUrlData = async (scheduleId) =>
    await getNoticeUrl(
      {
        endpoint: `/inspection-observation/schedule/${scheduleId}/print-notice`,
      },
      true
    ).unwrap();
  return [getNoticeUrlData, data];
};
